import type {TransactionStatus, TransactionType} from "@/types/transaction.types";

interface TransactionFiltersProps {
  typeFilter: TransactionType | "all";
  statusFilter: TransactionStatus | "all";
  searchQuery: string;
  onTypeChange: (type: TransactionType | "all") => void;
  onStatusChange: (status: TransactionStatus | "all") => void;
  onSearchChange: (query: string) => void;
  onReset: () => void;
}

export function TransactionFilters({
  typeFilter,
  statusFilter,
  searchQuery,
  onTypeChange,
  onStatusChange,
  onSearchChange,
  onReset,
}: TransactionFiltersProps) {
  const types: {value: TransactionType | "all"; label: string}[] = [
    {value: "all", label: "Todos"},
    {value: "remittance", label: "Remesas"},
    {value: "deposit", label: "Depósitos"},
    {value: "withdrawal", label: "Retiros"},
    {value: "p2p_transfer", label: "P2P"},
  ];

  const statuses: {value: TransactionStatus | "all"; label: string}[] = [
    {value: "all", label: "Todos"},
    {value: "completed", label: "Completado"},
    {value: "pending", label: "Pendiente"},
    {value: "processing", label: "Procesando"},
    {value: "failed", label: "Fallido"},
    {value: "refunded", label: "Reembolsado"},
    {value: "cancelled", label: "Cancelado"},
  ];

  const hasActiveFilters = typeFilter !== "all" || statusFilter !== "all" || searchQuery.trim() !== "";

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
      {/* Search */}
      <div className="relative flex-1 min-w-50">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-4 w-4 text-(--color-text-tertiary)"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
            />
          </svg>
        </div>
        <input
          type="search"
          placeholder="Buscar por ID, email, corredor..."
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm bg-(--color-surface) border border-(--color-border) rounded-lg text-(--color-text-primary) placeholder:text-(--color-text-tertiary) focus:outline-none focus:border-(--color-primary) transition-colors"
        />
      </div>

      {/* Type Filter */}
      <select
        value={typeFilter}
        onChange={e => onTypeChange(e.target.value as TransactionType | "all")}
        className="py-2 pl-3 pr-8 text-sm bg-(--color-surface) border border-(--color-border) rounded-lg text-(--color-text-primary) focus:outline-none focus:border-(--color-primary) cursor-pointer transition-colors appearance-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 0.75rem center",
        }}
      >
        {types.map(t => (
          <option
            key={t.value}
            value={t.value}
          >
            {t.label}
          </option>
        ))}
      </select>

      {/* Status Filter */}
      <select
        value={statusFilter}
        onChange={e => onStatusChange(e.target.value as TransactionStatus | "all")}
        className="py-2 pl-3 pr-8 text-sm bg-(--color-surface) border border-(--color-border) rounded-lg text-(--color-text-primary) focus:outline-none focus:border-(--color-primary) cursor-pointer transition-colors appearance-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 0.75rem center",
        }}
      >
        {statuses.map(s => (
          <option
            key={s.value}
            value={s.value}
          >
            {s.label}
          </option>
        ))}
      </select>

      {/* Reset */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={onReset}
          className="py-2 px-3 text-sm text-(--color-text-secondary) hover:text-(--color-text-primary) border border-(--color-border) rounded-lg hover:bg-(--color-surface-hover) transition-colors"
        >
          Limpiar
        </button>
      )}
    </div>
  );
}
